import React, { useState, useEffect } from 'react';
import {


  View,
  Text,
  TextInput,
  TouchableOpacity,
  ScrollView,
  Alert,
  ActivityIndicator,
  StyleSheet,
  Dimensions,
  KeyboardAvoidingView,
  Platform,
  StatusBar,
} from 'react-native';
import { apiFetch } from '../../utils/apiClient';
import useProfile from '../../hooks/useProfile';
import ProfileManagementService from '../../services/ProfileManagementService';
import PROFILE_KEYS from '../../constants/profileKeys';

// === API CONFIG ===
const API_BASE = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
// Bulletproof icon imports with fallbacks
const FallbackIcon = ({ name, size = 24, color = '#666', style, ...props }) => (
  <View 
    style={[
      {
        width: size,
        height: size,
        backgroundColor: color,
        borderRadius: size / 2,
        opacity: 0.3
      },
      style
    ]}
    {...props}
  />
);

let Ionicons = FallbackIcon;
let FontAwesome5 = FallbackIcon;
let MaterialIcons = FallbackIcon;

try {
  const iconsModule = require('@expo/vector-icons');
  if (iconsModule) {
    if (iconsModule.Ionicons && typeof iconsModule.Ionicons === 'function') {
      Ionicons = iconsModule.Ionicons;
    }
    if (iconsModule.FontAwesome5 && typeof iconsModule.FontAwesome5 === 'function') {
      FontAwesome5 = iconsModule.FontAwesome5;
    }
    if (iconsModule.MaterialIcons && typeof iconsModule.MaterialIcons === 'function') {
      MaterialIcons = iconsModule.MaterialIcons;
    }
  }
} catch (error) {
  console.warn('@expo/vector-icons not available, using fallback');
}

// Mock AsyncStorage for environments where it's not available
let AsyncStorage;
try {
  AsyncStorage = require('@react-native-async-storage/async-storage').default;
} catch (error) {
  console.warn('AsyncStorage not available, using mock implementation');
  AsyncStorage = {
    getItem: async (key) => null,
    setItem: async (key, value) => {},
    removeItem: async (key) => {}
  };
}

// Bulletproof service imports with fallbacks - NO DUPLICATE DECLARATIONS
class MockSkillService {
  async initialize() { return true; }
  
  async fetchSkill(id) {
    return new Promise(resolve => setTimeout(() => {
      resolve({
        id,
        name: 'JavaScript',
        category: 'Programming Languages',
        level: 'Advanced',
        experience: '5+ years',
        description: 'Proficient in modern JavaScript ES6+, including async/await, destructuring, and modules.',
        projects: [
          'E-commerce Platform',
          'Task Management App',
          'Real-time Chat Application'
        ],
        certifications: [
          'JavaScript Developer Certification',
          'Advanced JavaScript Concepts'
        ],
        lastUsed: '2024-01-15',
        proficiencyScore: 85,
        endorsements: 12,
        verified: true
      });
    }, 500));
  }
  
  async updateSkill(userId, skillData) {
    return new Promise(resolve => setTimeout(() => {
      resolve({
        success: true,
        message: 'Skill updated successfully'
      });
    }, 1000));
  }
  
  async deleteSkill(userId, skillId) {
    return new Promise(resolve => setTimeout(() => {
      resolve({
        success: true,
        message: 'Skill deleted successfully'
      });
    }, 1000));
  }
  
  validateSkillData(data) {
    const errors = {};
    if (!data.name || data.name.trim() === '') {
      errors.name = 'Skill name is required';
    }
    if (!data.category || data.category.trim() === '') {
      errors.category = 'Category is required';
    }
    if (!data.level || data.level.trim() === '') {
      errors.level = 'Skill level is required';
    }
    return { isValid: Object.keys(errors).length === 0, errors };
  }
}

class MockUnifiedService {
  async initialize() { return true; }
  async trackUserActivity() { return { success: true }; }
}

class MockDashboardService {
  async initialize() { return true; }
  async updateDashboardMetrics() { return { success: true }; }
}

// Import services with fallbacks - SINGLE DECLARATION
let SkillService = MockSkillService;
let UnifiedService = MockUnifiedService;
let DashboardService = MockDashboardService;

try {
  const skillModule = require('../../services/SkillManagementService');
  if (skillModule && skillModule.default) {
    SkillService = skillModule.default;
  } else if (skillModule && typeof skillModule === 'function') {
    SkillService = skillModule;
  }
} catch (error) {
  console.warn('SkillManagementService not available, using mock');
}

try {
  const unifiedModule = require('../../services/UnifiedIntegrationService');
  if (unifiedModule && unifiedModule.default) {
    UnifiedService = unifiedModule.default;
  } else if (unifiedModule && typeof unifiedModule === 'function') {
    UnifiedService = unifiedModule;
  }
} catch (error) {
  console.warn('UnifiedIntegrationService not available, using mock');
}

try {
  const dashboardModule = require('../../services/DashboardIntegrationService');
  if (dashboardModule && dashboardModule.default) {
    DashboardService = dashboardModule.default;
  } else if (dashboardModule && typeof dashboardModule === 'function') {
    DashboardService = dashboardModule;
  }
} catch (error) {
  console.warn('DashboardIntegrationService not available, using mock');
}

const { width, height } = Dimensions.get('window');

const EditSkill = ({ navigation, route }) => {
  // ── Profile integration (auto-injected) ──
  const profileCtx = useProfile();
  // Get skill data from navigation params
  const skillData = route.params?.skill || {};
  const isEditing = !!skillData.id;

  // Services - USING RENAMED VARIABLES
  const [skillService] = useState(() => new SkillService());
  const [unifiedService] = useState(() => new UnifiedService());
  const [dashboardService] = useState(() => new DashboardService());

  // State management
  const [skill, setSkill] = useState({
    id: skillData.id || null,
    name: skillData.name || '',
    category: skillData.category || '',
    level: skillData.level || '',
    experience: skillData.experience || '',
    description: skillData.description || '',
    projects: skillData.projects || [''],
    certifications: skillData.certifications || [''],
    lastUsed: skillData.lastUsed || '',
    proficiencyScore: skillData.proficiencyScore || 50,
    endorsements: skillData.endorsements || 0,
    verified: skillData.verified || false,
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [hasChanges, setHasChanges] = useState(false);

  // Skill categories
  const skillCategories = [
    'Programming Languages',
    'Web Development',
    'Mobile Development',
    'Database Management',
    'Cloud Computing',
    'DevOps',
    'Design',
    'Project Management',
    'Data Science',
    'Machine Learning',
    'Cybersecurity',
    'Other'
  ];

  // Skill levels
  const skillLevels = [
    'Beginner',
    'Intermediate',
    'Advanced',
    'Expert'
  ];

  useEffect(() => {
    initializeServices();
    if (isEditing && skillData.id) {
      loadSkillData();
    }
  }, []);

  useEffect(() => {
    // Check if there are changes compared to original data
    const hasSkillChanges = JSON.stringify(skill) !== JSON.stringify({
      id: skillData.id || null,
      name: skillData.name || '',
      category: skillData.category || '',
      level: skillData.level || '',
      experience: skillData.experience || '',
      description: skillData.description || '',
      projects: skillData.projects || [''],
      certifications: skillData.certifications || [''],
      lastUsed: skillData.lastUsed || '',
      proficiencyScore: skillData.proficiencyScore || 50,
      endorsements: skillData.endorsements || 0,
      verified: skillData.verified || false,
    });
    setHasChanges(hasSkillChanges);
  }, [skill, skillData]);

  const initializeServices = async () => {
    try {
      await Promise.all([
        skillService.initialize().catch(e => console.warn('Skill service init failed:', e)),
        unifiedService.initialize().catch(e => console.warn('Unified service init failed:', e)),
        dashboardService.initialize().catch(e => console.warn('Dashboard service init failed:', e))
      ]);

      // Track screen view
      try {
        await unifiedService.trackUserActivity(isEditing ? 'edit_skill_viewed' : 'add_skill_viewed');
      } catch (error) {
        console.warn('Failed to track activity:', error);
      }
    } catch (error) {
      console.error('Error initializing services:', error);
    }
  };

  const loadSkillData = async () => {
    if (!isEditing) return;

    setLoading(true);
    try {
      const result = await skillService.fetchSkill(skillData.id);
      if (result) {
        setSkill(result);
      }
    } catch (error) {
      console.error('Error loading skill data:', error);
      Alert.alert('Error', 'Failed to load skill data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const validateInput = () => {
    const validation = skillService.validateSkillData(skill);
    setErrors(validation.errors);
    return validation.isValid;
  };

  const handleSaveSkill = async () => {
    console.log('Save skill initiated...');
    
    if (!validateInput()) {
      Alert.alert('Validation Error', 'Please correct the errors in the form.');
      return;
    }

    setSaving(true);
    
    try {
      const userId = 'demo_user'; // In real app, get from auth context
      const result = await skillService.updateSkill(userId, skill);
      
      if (result.success) {
        // Save to cache
        const cacheKey = isEditing ? 'editedSkill' : 'newSkill';
        await AsyncStorage.setItem(cacheKey, JSON.stringify(skill));
        
        // Track activity
        try {
          await unifiedService.trackUserActivity(isEditing ? 'skill_updated' : 'skill_added');
          await dashboardService.updateDashboardMetrics('skill_updated', { userId });
        } catch (error) {
          console.warn('Failed to track activity:', error);
        }
        
        // Show success message and navigate back
        Alert.alert(
          'Success',
          result.message || `Skill ${isEditing ? 'updated' : 'added'} successfully!`,
          [
            {
              text: 'OK',
              onPress: () => {
                if (navigation.canGoBack()) {
                  navigation.goBack();
                } else {
                  navigation.navigate('AllSkillsScreen');
                }
              }
            }
          ]
        );
      } else {
        throw new Error(result.error || 'Failed to save skill');
      }
      
    } catch (error) {
      console.error('Error saving skill:', error);
      Alert.alert('Error', error.message || 'Failed to save skill. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSkill = async () => {
    if (!isEditing) return;

    Alert.alert(
      'Delete Skill',
      'Are you sure you want to delete this skill?',
      [
        { text: 'Cancel', style: 'cancel' },
        { 
          text: 'Delete', 
          style: 'destructive',
          onPress: async () => {
            setSaving(true);
            try {
              const userId = 'demo_user';
              const result = await skillService.deleteSkill(userId, skill.id);
              
              if (result.success) {
                // Track activity
                try {
                  await unifiedService.trackUserActivity('skill_deleted');
                  await dashboardService.updateDashboardMetrics('skill_deleted', { userId });
                } catch (error) {
                  console.warn('Failed to track activity:', error);
                }
                
                Alert.alert('Success', 'Skill deleted successfully!', [
                  { text: 'OK', onPress: () => navigation.goBack() }
                ]);
              } else {
                throw new Error(result.error || 'Failed to delete skill');
              }
            } catch (error) {
              console.error('Error deleting skill:', error);
              Alert.alert('Error', 'Failed to delete skill. Please try again.');
            } finally {
              setSaving(false);
            }
          }
        }
      ]
    );
  };

  const handleBackPress = () => {
    if (hasChanges) {
      Alert.alert(
        'Discard Changes',
        'Are you sure you want to discard your changes?',
        [
          { text: 'Keep Editing', style: 'cancel' },
          { 
            text: 'Discard', 
            style: 'destructive', 
            onPress: () => {
              if (navigation.canGoBack()) {
                navigation.goBack();
              } else {
                navigation.navigate('AllSkillsScreen');
              }
            }
          }
        ]
      );
    } else {
      if (navigation.canGoBack()) {
        navigation.goBack();
      } else {
        navigation.navigate('AllSkillsScreen');
      }
    }
  };

  const updateSkill = (field, value) => {
    setSkill({ ...skill, [field]: value });
  };

  const addProject = () => {
    setSkill({
      ...skill,
      projects: [...skill.projects, '']
    });
  };

  const updateProject = (index, value) => {
    const newProjects = [...skill.projects];
    newProjects[index] = value;
    setSkill({ ...skill, projects: newProjects });
  };

  const removeProject = (index) => {
    if (skill.projects.length > 1) {
      const newProjects = skill.projects.filter((_, i) => i !== index);
      setSkill({ ...skill, projects: newProjects });
    }
  };

  const addCertification = () => {
    setSkill({
      ...skill,
      certifications: [...skill.certifications, '']
    });
  };

  const updateCertification = (index, value) => {
    const newCertifications = [...skill.certifications];
    newCertifications[index] = value;
    setSkill({ ...skill, certifications: newCertifications });
  };

  const removeCertification = (index) => {
    if (skill.certifications.length > 1) {
      const newCertifications = skill.certifications.filter((_, i) => i !== index);
      setSkill({ ...skill, certifications: newCertifications });
    }
  };

  const getProficiencyColor = (score) => {
    if (score >= 80) return '#4CAF50';
    if (score >= 60) return '#FF9800';
    if (score >= 40) return '#FFC107';
    return '#f44336';
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <StatusBar barStyle="dark-content" backgroundColor="#fff" />
        <ActivityIndicator size={36} color="#4CAF50" />
        <Text style={styles.loadingText}>Loading Skill...</Text>
      </View>
    );
  }

  return (
    <KeyboardAvoidingView 
      style={styles.container}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
    >
      <StatusBar barStyle="dark-content" backgroundColor="#fff" />
      
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity style={styles.backButton} onPress={handleBackPress}>
          <Ionicons name="arrow-back" size={24} color="#333" />
        </TouchableOpacity>
        
        <Text style={styles.headerTitle}>
          {isEditing ? 'Edit Skill' : 'Add Skill'}
        </Text>
        
        <View style={styles.headerActions}>
          {isEditing && (
            <TouchableOpacity 
              style={styles.deleteButton}
              onPress={handleDeleteSkill}
              disabled={saving}
            >
              <Ionicons name="trash-outline" size={20} color="#f44336" />
            </TouchableOpacity>
          )}
        </View>
      </View>

      <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
        {/* Basic Information */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Basic Information</Text>
          
          <View style={styles.inputContainer}>
            <Text style={styles.label}>Skill Name *</Text>
            <TextInput
              style={[styles.input, errors.name && styles.inputError]}
              value={skill.name}
              onChangeText={(text) => updateSkill('name', text)}
              placeholder="e.g., JavaScript, React, Python"
              autoCapitalize="words"
            />
            {errors.name && <Text style={styles.errorText}>{errors.name}</Text>}
          </View>

          <View style={styles.inputContainer}>
            <Text style={styles.label}>Category *</Text>
            <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.categoryScroll}>
              {skillCategories.map((category) => (
                <TouchableOpacity
                  key={category}
                  style={[
                    styles.categoryChip,
                    skill.category === category && styles.categoryChipSelected
                  ]}
                  onPress={() => updateSkill('category', category)}
                >
                  <Text style={[
                    styles.categoryChipText,
                    skill.category === category && styles.categoryChipTextSelected
                  ]}>
                    {category}
                  </Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
            {errors.category && <Text style={styles.errorText}>{errors.category}</Text>}
          </View>

          <View style={styles.inputContainer}>
            <Text style={styles.label}>Skill Level *</Text>
            <View style={styles.levelContainer}>
              {skillLevels.map((level) => (
                <TouchableOpacity
                  key={level}
                  style={[
                    styles.levelChip,
                    skill.level === level && styles.levelChipSelected
                  ]}
                  onPress={() => updateSkill('level', level)}
                >
                  <Text style={[
                    styles.levelChipText,
                    skill.level === level && styles.levelChipTextSelected
                  ]}>
                    {level}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
            {errors.level && <Text style={styles.errorText}>{errors.level}</Text>}
          </View>

          <View style={styles.inputContainer}>
            <Text style={styles.label}>Experience</Text>
            <TextInput
              style={styles.input}
              value={skill.experience}
              onChangeText={(text) => updateSkill('experience', text)}
              placeholder="e.g., 3 years, 6 months"
            />
          </View>
        </View>

        {/* Proficiency Score */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Proficiency Assessment</Text>
          
          <View style={styles.proficiencyContainer}>
            <Text style={styles.proficiencyLabel}>
              Proficiency Score: {skill.proficiencyScore}%
            </Text>
            <View style={styles.proficiencyBar}>
              <View 
                style={[
                  styles.proficiencyBarFill,
                  { 
                    width: `${skill.proficiencyScore}%`,
                    backgroundColor: getProficiencyColor(skill.proficiencyScore)
                  }
                ]}
              />
            </View>
            <View style={styles.proficiencyControls}>
              <TouchableOpacity
                style={styles.proficiencyButton}
                onPress={() => updateSkill('proficiencyScore', Math.max(0, skill.proficiencyScore - 10))}
              >
                <Ionicons name="remove" size={20} color="#666" />
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.proficiencyButton}
                onPress={() => updateSkill('proficiencyScore', Math.min(100, skill.proficiencyScore + 10))}
              >
                <Ionicons name="add" size={20} color="#666" />
              </TouchableOpacity>
            </View>
          </View>
        </View>

        {/* Description */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Description</Text>
          
          <View style={styles.inputContainer}>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={skill.description}
              onChangeText={(text) => updateSkill('description', text)}
              placeholder="Describe your experience with this skill, key achievements, and specific technologies or frameworks you've used..."
              multiline
              numberOfLines={4}
              textAlignVertical="top"
            />
          </View>
        </View>

        {/* Projects */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Projects Using This Skill</Text>
          
          {skill.projects.map((project, index) => (
            <View key={index} style={styles.dynamicInputContainer}>
              <TextInput
                style={styles.input}
                value={project}
                onChangeText={(text) => updateProject(index, text)}
                placeholder={`Project ${index + 1}`}
                autoCapitalize="words"
              />
              <View style={styles.dynamicInputActions}>
                {skill.projects.length > 1 && (
                  <TouchableOpacity
                    style={styles.removeButton}
                    onPress={() => removeProject(index)}
                  >
                    <Ionicons name="remove-circle" size={20} color="#f44336" />
                  </TouchableOpacity>
                )}
              </View>
            </View>
          ))}
          
          <TouchableOpacity style={styles.addButton} onPress={addProject}>
            <Ionicons name="add-circle" size={20} color="#4CAF50" />
            <Text style={styles.addButtonText}>Add Project</Text>
          </TouchableOpacity>
        </View>

        {/* Certifications */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Related Certifications</Text>
          
          {skill.certifications.map((certification, index) => (
            <View key={index} style={styles.dynamicInputContainer}>
              <TextInput
                style={styles.input}
                value={certification}
                onChangeText={(text) => updateCertification(index, text)}
                placeholder={`Certification ${index + 1}`}
                autoCapitalize="words"
              />
              <View style={styles.dynamicInputActions}>
                {skill.certifications.length > 1 && (
                  <TouchableOpacity
                    style={styles.removeButton}
                    onPress={() => removeCertification(index)}
                  >
                    <Ionicons name="remove-circle" size={20} color="#f44336" />
                  </TouchableOpacity>
                )}
              </View>
            </View>
          ))}
          
          <TouchableOpacity style={styles.addButton} onPress={addCertification}>
            <Ionicons name="add-circle" size={20} color="#4CAF50" />
            <Text style={styles.addButtonText}>Add Certification</Text>
          </TouchableOpacity>
        </View>

        {/* Additional Details */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Additional Details</Text>
          
          <View style={styles.inputContainer}>
            <Text style={styles.label}>Last Used</Text>
            <TextInput
              style={styles.input}
              value={skill.lastUsed}
              onChangeText={(text) => updateSkill('lastUsed', text)}
              placeholder="YYYY-MM-DD or description"
            />
          </View>

          <View style={styles.statsContainer}>
            <View style={styles.statItem}>
              <Text style={styles.statLabel}>Endorsements</Text>
              <View style={styles.statControls}>
                <TouchableOpacity
                  style={styles.statButton}
                  onPress={() => updateSkill('endorsements', Math.max(0, skill.endorsements - 1))}
                >
                  <Ionicons name="remove" size={16} color="#666" />
                </TouchableOpacity>
                <Text style={styles.statValue}>{skill.endorsements}</Text>
                <TouchableOpacity
                  style={styles.statButton}
                  onPress={() => updateSkill('endorsements', skill.endorsements + 1)}
                >
                  <Ionicons name="add" size={16} color="#666" />
                </TouchableOpacity>
              </View>
            </View>

            <TouchableOpacity 
              style={styles.verifiedContainer}
              onPress={() => updateSkill('verified', !skill.verified)}
            >
              <View style={[styles.checkbox, skill.verified && styles.checkboxChecked]}>
                {skill.verified && (
                  <Ionicons name="checkmark" size={16} color="#fff" />
                )}
              </View>
              <Text style={styles.verifiedLabel}>Verified Skill</Text>
            </TouchableOpacity>
          </View>
        </View>

        {/* Save Button */}
        <TouchableOpacity
          style={[styles.saveButton, (!hasChanges || saving) && styles.saveButtonDisabled]}
          onPress={handleSaveSkill}
          disabled={!hasChanges || saving}
        >
          {saving ? (
            <ActivityIndicator color="#fff" />
          ) : (
            <Text style={styles.saveButtonText}>
              {isEditing ? 'Update Skill' : 'Add Skill'}
            </Text>
          )}
        </TouchableOpacity>
      </ScrollView>
    </KeyboardAvoidingView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f5f5f5',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#666',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    paddingTop: 50,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  backButton: {
    padding: 8,
    borderRadius: 20,
    backgroundColor: '#f8f9fa',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  deleteButton: {
    padding: 8,
    borderRadius: 20,
    backgroundColor: '#ffebee',
  },
  content: {
    flex: 1,
    padding: 20,
  },
  section: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 20,
    marginBottom: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 20,
  },
  inputContainer: {
    marginBottom: 20,
  },
  label: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginBottom: 8,
  },
  input: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    backgroundColor: '#fff',
  },
  inputError: {
    borderColor: '#f44336',
    backgroundColor: '#ffebee',
  },
  textArea: {
    minHeight: 100,
    textAlignVertical: 'top',
  },
  errorText: {
    color: '#f44336',
    fontSize: 14,
    marginTop: 4,
  },
  categoryScroll: {
    flexDirection: 'row',
  },
  categoryChip: {
    backgroundColor: '#f0f0f0',
    borderRadius: 20,
    paddingHorizontal: 16,
    paddingVertical: 8,
    marginRight: 10,
    borderWidth: 1,
    borderColor: '#ddd',
  },
  categoryChipSelected: {
    backgroundColor: '#4CAF50',
    borderColor: '#4CAF50',
  },
  categoryChipText: {
    fontSize: 14,
    color: '#666',
    textAlign: 'center',
  },
  categoryChipTextSelected: {
    color: '#fff',
    fontWeight: '600',
  },
  levelContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
  },
  levelChip: {
    backgroundColor: '#f0f0f0',
    borderRadius: 16,
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderWidth: 1,
    borderColor: '#ddd',
    minWidth: (width - 80) / 2,
    alignItems: 'center',
  },
  levelChipSelected: {
    backgroundColor: '#2196F3',
    borderColor: '#2196F3',
  },
  levelChipText: {
    fontSize: 14,
    color: '#666',
  },
  levelChipTextSelected: {
    color: '#fff',
    fontWeight: '600',
  },
  proficiencyContainer: {
    alignItems: 'center',
  },
  proficiencyLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginBottom: 12,
  },
  proficiencyBar: {
    width: '100%',
    height: 12,
    backgroundColor: '#e0e0e0',
    borderRadius: 6,
    marginBottom: 16,
  },
  proficiencyBarFill: {
    height: '100%',
    borderRadius: 6,
  },
  proficiencyControls: {
    flexDirection: 'row',
    gap: 20,
  },
  proficiencyButton: {
    backgroundColor: '#f8f9fa',
    borderRadius: 20,
    padding: 8,
    borderWidth: 1,
    borderColor: '#e9ecef',
  },
  dynamicInputContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  dynamicInputActions: {
    marginLeft: 12,
    justifyContent: 'center',
  },
  removeButton: {
    padding: 4,
  },
  addButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    backgroundColor: '#f8f9fa',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e9ecef',
    borderStyle: 'dashed',
  },
  addButtonText: {
    fontSize: 16,
    color: '#4CAF50',
    fontWeight: '600',
    marginLeft: 8,
  },
  statsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  statItem: {
    alignItems: 'center',
  },
  statLabel: {
    fontSize: 14,
    color: '#666',
    marginBottom: 8,
  },
  statControls: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  statButton: {
    backgroundColor: '#f8f9fa',
    borderRadius: 16,
    padding: 6,
    borderWidth: 1,
    borderColor: '#e9ecef',
  },
  statValue: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
    minWidth: 30,
    textAlign: 'center',
  },
  verifiedContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  checkbox: {
    width: 20,
    height: 20,
    borderWidth: 2,
    borderColor: '#ddd',
    borderRadius: 4,
    marginRight: 8,
    justifyContent: 'center',
    alignItems: 'center',
  },
  checkboxChecked: {
    backgroundColor: '#4CAF50',
    borderColor: '#4CAF50',
  },
  verifiedLabel: {
    fontSize: 16,
    color: '#333',
  },
  saveButton: {
    backgroundColor: '#4CAF50',
    borderRadius: 12,
    padding: 16,
    alignItems: 'center',
    marginTop: 20,
    marginBottom: 40,
  },
  saveButtonDisabled: {
    backgroundColor: '#ccc',
  },
  saveButtonText: {
    color: '#fff',
    fontSize: 18,
    fontWeight: 'bold',
  },
});

export default EditSkill;
